import {Component, EventEmitter, Input, Output} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import {Country, EventItem, Lookups, Race} from "@model";
import {LiveItemStatus} from "../../model/live-item-status";
import dayjs from "dayjs";
import {environment} from "@environment";
import {localizedDateString} from "@utility";
import {
  LIVE_RESULTS, LIVE_RESULTS_PHOTO_POINT,
  PRIVATE_LIVE_RESULTS, PRIVATE_LIVE_RESULTS_PHOTO_POINT
} from "../../app-routing.module";
import {ActivatedRoute, Router} from "@angular/router";
import {AuthenticationService} from "@services";

@Component({
    selector: 'app-live-results-heading',
    templateUrl: './live-results-race-heading.component.html',
    styleUrls: ['./live-results-race-heading.component.scss'],
    standalone: false
})
export class LiveResultsRaceHeadingComponent {
  @Input() type: LiveResultHeadingType = LiveResultHeadingType.EVENT;
  @Input() event: EventItem | undefined = undefined;
  @Input() race: Race | undefined = undefined;
  @Input() finished: number = 0;
  @Input() started: number = 0;

  @Input() darkMode: boolean = false;
  @Input() isSplitScreen: boolean = false;
  @Input() isFullScreen: boolean = false;
  @Input() showRanking: boolean = false;

  @Input({required: true}) languages: Lookups[] = [];
  @Input({required: true}) countries: Country[] = [];
  @Output() onClick = new EventEmitter<void>();
  @Output() onToggleDarkMode = new EventEmitter<boolean>();
  @Output() onSplitScreen = new EventEmitter<boolean>();
  @Output() onShowRankingToggle = new EventEmitter<boolean>();

  status: LiveItemStatus = LiveItemStatus.TO_BE_STARTED
  dateString: string = "";
  country: string | undefined;

  constructor(public translocoService: TranslocoService,
              private router: Router,
              public authenticationService: AuthenticationService,
              private route: ActivatedRoute,
  ) {
  }

  ngOnInit() {
    this.formatDate();
    this.getCountryName(this.race?.startCountry ?? this.event?.country);
    if (this.type === LiveResultHeadingType.EVENT) {
      this.startTimer(this.event?.start, this.event?.end)
    } else {
      this.startTimer(this.race?.raceStart, this.race?.raceEnd)
    }
  }


  formatDate() {
    const start = this.type === LiveResultHeadingType.RACE ? this.race?.raceStart : this.event?.start;
    const end = this.type === LiveResultHeadingType.RACE ? this.race?.raceEnd : this.event?.end;
    if (start && end) {
      const lang: string = this.translocoService.getActiveLang();
      if (this.type === LiveResultHeadingType.EVENT) {
        if (dayjs(start).isSame(dayjs(end), 'days')) {
          this.dateString = `${localizedDateString(start, 'short', lang)} - ${localizedDateString(end, "HH:mm", lang)}`
        } else {
          this.dateString = `${localizedDateString(start, 'short', lang)} - ${localizedDateString(end, 'short', lang)}`
        }
      }
      if (this.type === LiveResultHeadingType.RACE) {
        this.dateString = `${localizedDateString(start, 'shortDate', lang)} ${localizedDateString(start, 'HH:mm', lang)}`
      }
    }
  }


  private startTimer(startDate: Date | undefined, endDate: Date | undefined): void {
    if (startDate && endDate) {
      const start = new Date(startDate).getTime();
      const end = new Date(endDate).getTime();
      const now = new Date().getTime();
      const limit = environment.dayInSeconds;
      if (now < start) {
        this.status = LiveItemStatus.TO_BE_STARTED;
        if (now + limit > start) {
          setTimeout(() => {
            this.status = LiveItemStatus.IN_PROGRESS;
            if (limit > end - start) {
              setTimeout(() => {
                this.status = LiveItemStatus.FINISHED;
              }, end - start);
            }
          }, start - now);
        }
        return
      }
      if (now > end) {
        this.status = LiveItemStatus.FINISHED;
        return
      } else {
        this.status = LiveItemStatus.IN_PROGRESS;
        if (now + limit > end) {
          setTimeout(() => {
            this.status = LiveItemStatus.FINISHED;
          }, end - now);
        }
        return;
      }
    }
  }

  onHeadingClick() {
    this.onClick.emit()
  }

  getCountryName(countryCode: number | undefined) {
    this.countries?.find((country: Country) => {
      if (countryCode === country.id) {
        this.country = this.translocoService.translate("app.common.country." + country.id);
      }
    });
  }

  protected readonly LiveItemStatus = LiveItemStatus;
  protected readonly LiveResultHeadingType = LiveResultHeadingType;

  toggleDarkMode() {
    if (this.isFullScreen) {
      this.darkMode = !this.darkMode;
      this.onToggleDarkMode.emit(this.darkMode);
    }
  }

  toggleSplitScreen() {
    if (this.isFullScreen) {
        this.isSplitScreen = !this.isSplitScreen;
        this.onSplitScreen.emit(this.isSplitScreen);
    }
  }

  toggleRanking() {
    if (this.isFullScreen) {
        this.showRanking = !this.showRanking;
        this.onShowRankingToggle.emit(this.showRanking);
    }
  }


  goToLiveResults() {
    this.router.navigate([this.authenticationService.isAuthenticated() ? PRIVATE_LIVE_RESULTS : LIVE_RESULTS]);
  }

  openPhotoPointPage() {
    this.router.navigate([this.authenticationService.isAuthenticated() ? PRIVATE_LIVE_RESULTS_PHOTO_POINT : LIVE_RESULTS_PHOTO_POINT], {
      queryParams: {
        eventId: this.event?.id,
      }
    });
  }

}

export enum LiveResultHeadingType {
  RACE,
  EVENT
}
